/**
 * <PremiumGate> — universal wrapper for Premium-only content.
 *
 * Free users see the children rendered but heavily blurred + a CTA
 * overlay "🔒 Premium · Sblocca". On click the user is taken to
 * /pricing. Premium / lifetime / grandfathered users see children as-is.
 *
 * Props:
 *   - feature   string  label shown in the overlay (default "Funzionalità")
 *   - reason    string  optional short explanation (e.g. "Filtro avanzato")
 *   - mode      "blur" | "hide"  default "blur"
 *   - cta       string  default "Sblocca con Premium"
 *   - children  ReactNode
 *   - inline    boolean if true, smaller compact overlay (e.g. inside a chip)
 *   - className optional css class for the outer wrapper
 *   - style     optional inline style for the outer wrapper
 *   - onUpgrade optional handler invoked instead of navigating to /pricing
 */
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { usePremium } from "@/contexts/AuthContext";

const ACCENT = "#C8FF00";

export default function PremiumGate({
  feature = "Funzionalità",
  reason = null,
  mode = "blur",
  cta = "Sblocca Premium",
  children,
  inline = false,
  className = "",
  style = {},
  onUpgrade = null,
  testid = null,
  forceUnlock = false,
}) {
  const navigate = useNavigate();
  const { isPremium, loading } = usePremium();

  if (loading) return null;
  if (isPremium || forceUnlock) return <>{children}</>;

  const handleUpgrade = (e) => {
    e?.stopPropagation?.();
    if (onUpgrade) onUpgrade();
    else navigate("/pricing");
  };

  if (mode === "hide") {
    return (
      <div
        onClick={handleUpgrade}
        data-testid={testid || "premium-gate-hide"}
        className={className}
        style={{
          cursor: "pointer",
          padding: inline ? "0.55rem 0.85rem" : "1rem 1.2rem",
          border: `1px dashed ${ACCENT}66`,
          background: "rgba(200,255,0,0.04)",
          color: ACCENT,
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: inline ? "0.66rem" : "0.74rem",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          transition: "background 0.15s, box-shadow 0.15s",
          ...style,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(200,255,0,0.10)";
          e.currentTarget.style.boxShadow = `0 0 18px ${ACCENT}44`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(200,255,0,0.04)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <Lock size={inline ? 11 : 13} />
        <span>{feature} · Premium</span>
        <Sparkles size={inline ? 10 : 12} style={{ marginLeft: 4 }} />
      </div>
    );
  }

  // mode === "blur"
  return (
    <div
      className={className}
      style={{ position: "relative", ...style }}
      data-testid={testid || "premium-gate-blur"}
    >
      <div
        aria-hidden
        style={{
          filter: inline ? "blur(4px) saturate(0.6)" : "blur(7px) saturate(0.5)",
          pointerEvents: "none",
          userSelect: "none",
          opacity: 0.85,
        }}
      >
        {children}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          padding: "1rem",
        }}
      >
        <button
          type="button"
          onClick={handleUpgrade}
          data-testid={testid ? `${testid}-cta` : "premium-gate-cta"}
          style={{
            pointerEvents: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: inline ? 4 : 8,
            padding: inline ? "0.5rem 0.85rem" : "0.85rem 1.2rem",
            maxWidth: "92%",
            background: "rgba(8,8,10,0.92)",
            border: `1px solid ${ACCENT}`,
            color: ACCENT,
            fontFamily: "'Barlow Condensed',sans-serif",
            fontWeight: 800,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontSize: inline ? "0.7rem" : "0.85rem",
            cursor: "pointer",
            boxShadow: `0 0 22px ${ACCENT}55, inset 0 0 12px ${ACCENT}22`,
            transition: "transform 0.15s, box-shadow 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.04)";
            e.currentTarget.style.boxShadow = `0 0 32px ${ACCENT}99, inset 0 0 14px ${ACCENT}44`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = `0 0 22px ${ACCENT}55, inset 0 0 12px ${ACCENT}22`;
          }}
        >
          <Lock size={inline ? 12 : 16} />
          <div>{feature}</div>
          {reason && !inline && (
            <div style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.6rem", letterSpacing: "0.10em",
              color: "rgba(200,255,0,0.7)", textTransform: "none",
            }}>
              {reason}
            </div>
          )}
          <div style={{
            marginTop: inline ? 0 : 2,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: inline ? "0.55rem" : "0.62rem",
            letterSpacing: "0.12em",
            color: "#FFFFFF",
          }}>
            <Sparkles size={inline ? 9 : 10} style={{ display: "inline", marginRight: 4 }} />
            {cta}
          </div>
        </button>
      </div>
    </div>
  );
}

/**
 * Inline lock chip — small badge to show next to a Premium-only filter/toggle.
 */
export function PremiumLockChip({ label = "Premium", onClick = null }) {
  const navigate = useNavigate();
  const { isPremium } = usePremium();
  if (isPremium) return null;
  const handle = (e) => {
    e?.stopPropagation?.();
    if (onClick) onClick(e); else navigate("/pricing");
  };
  return (
    <span
      onClick={handle}
      data-testid="premium-lock-chip"
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "0.15rem 0.45rem",
        background: "rgba(200,255,0,0.10)",
        border: `1px solid ${ACCENT}66`,
        color: ACCENT,
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: "0.55rem",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        cursor: "pointer",
        verticalAlign: "middle",
      }}
    >
      <Lock size={9} /> {label}
    </span>
  );
}
